const INTERACTIVE = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  'label',
  '[contenteditable]:not([contenteditable="false" i])',
  ...['button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'radio', 'switch', 'combobox'].map(
    (role) => `[role="${role}"]`
  ),
].join(',');

const FORM_CONTROLS = new Set(['input', 'select', 'textarea', 'button']);
// Route slugs such as `tuition-assistance`; any other segment could be an id, a token or an
// encoded email, and is templated.
const WORD_SLUG = /^[a-z]+(?:-[a-z]+)*$/;
const MAX_LENGTH = 200;

/**
 * The control a click was aimed at: the nearest interactive ancestor of the element the
 * pointer landed on (an icon inside a button resolves to the button), or that element
 * itself when nothing interactive encloses it.
 */
export function clickTarget(element: Element): Element {
  return element.closest(INTERACTIVE) ?? element;
}

/**
 * A selector-like description of a click target built only from tokens a developer chose:
 * tag, classes without digits, `role`, `type`, form-control `name`, `data-test-*` attribute
 * names and, for same-origin links, the path with every segment that is not a lowercase word
 * slug templated as `:id` (other links keep only their origin). Visible text, `id`, `aria-label` and any attribute value
 * that could carry a person's data are left out, so the result is safe to store and group by.
 */
export function describeClickTarget(element: Element): string {
  const tag = element.tagName.toLowerCase();
  const classes = Array.from(element.classList)
    .filter((name) => !/\d/.test(name))
    .slice(0, 3)
    .map((name) => `.${name}`)
    .join('');
  const attributes = [
    attribute('role', element.getAttribute('role')),
    FORM_CONTROLS.has(tag) ? attribute('type', element.getAttribute('type')) : '',
    FORM_CONTROLS.has(tag) ? attribute('name', element.getAttribute('name')) : '',
    tag === 'a' ? attribute('href', linkPath(element.getAttribute('href'))) : '',
    ...element
      .getAttributeNames()
      .filter((name) => name.startsWith('data-test-') && !/\d/.test(name))
      .slice(0, 2)
      .map((name) => `[${name}]`),
  ].join('');

  return `${tag}${classes}${attributes}`.slice(0, MAX_LENGTH);
}

function attribute(name: string, value: string | null) {
  return value ? `[${name}="${value.replace(/"/g, '')}"]` : '';
}

function linkPath(href: string | null) {
  if (!href) return null;

  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return url.protocol;

  if (url.origin !== window.location.origin) return url.origin;

  return url.pathname
    .split('/')
    .map((segment) => (!segment || WORD_SLUG.test(segment) ? segment : ':id'))
    .join('/');
}
